import React from 'react';

interface LegendCardProps {
  title: React.ReactNode;
  children: React.ReactNode;
  // When true, the card positions itself in the map's bottom-left corner.
  // Set false for legends that are placed inside a parent-controlled stack
  // (Species, AGB, SoilCarbon — grouped with sliders/toggles).
  positioned?: boolean;
  // Optional width / max-width utility classes applied alongside the base
  // card styles (e.g. `w-[220px]`, `max-w-[440px]`).
  className?: string;
}

/**
 * Shared chrome for every map legend — the translucent figma card shell plus
 * the small uppercase mono title. Body content fills `children`. Used by
 * Whp/Sd/Fo/Species/SoilCarbon/CanopyHeight legends; consolidates what was
 * previously ~6 lines of identical wrapper markup per legend.
 */
export const LegendCard: React.FC<LegendCardProps> = ({
  title,
  children,
  positioned = false,
  className = '',
}) => {
  const base =
    'bg-figma-card/90 border border-figma-lime/30 rounded-lg px-3 py-2 shadow-figma-card backdrop-blur-sm';
  // On mobile, lift above the bottom-sheet peek bar via the
  // `--mobile-peek-clearance` custom property (defined in globals.css —
  // tracks the bar height + iOS safe-area-inset). md+ resets the
  // variable to 16 px so the historical bottom-4 inset is preserved.
  // `right-4 md:right-auto` keeps wide legends (e.g. Forest Ownership's
  // 7-class chip row) from overflowing the viewport on mobile — the card
  // stretches to the visible width and its inner flex-wrap row reflows.
  // Desktop sizing is unchanged (right-auto → natural content width).
  const position = positioned
    ? 'absolute bottom-[var(--mobile-peek-clearance)] left-4 right-4 md:right-auto z-[500]'
    : '';
  return (
    <div className={`${position} ${base} ${className}`.trim()}>
      <div className="text-[11px] font-roboto-mono uppercase tracking-wider text-neutral-400 mb-1.5">
        {title}
      </div>
      {children}
    </div>
  );
};
